<template>
    <div
        class="oembed embed-responsive"
        :style="{ height: `${height}px` }"
    >
        <div v-if="isLoading || isTranscoding" class="loading">
            <template v-if="metadata && metadata.status === 'PROGRESSING'">
                <ProgressBar :value="metadata.percent" />
                <Txt
                    v-if="isTranscoding && component.width > 120"
                    size="xxxs" heading
                >
                    {{ $trans('vimeo_transcoding.detail') }}
                </Txt>
            </template>
            <template v-else>
                <Spinner spinner-class="white" />
                <Txt
                    v-if="isTranscoding && component.width > 120"
                    size="xxxs" heading
                >
                    {{ $trans('vimeo_transcoding.queue') }}
                </Txt>
            </template>
        </div>
        <Mux
            v-else-if="isMux"
            ref="mux"
            :src="src"
            :poster="poster"
            :metadata="metadata"
            :controls="controls"
            v-bind="$attrs"
            @duration="$emit('duration', $event)"
            @played="$emit('played', $event)"
        />
        <Movie
            v-else-if="isLegacyVod"
            ref="movie"
            :src="src"
            :poster="poster"
            :metadata="metadata"
            :controls="controls"
            v-bind="$attrs"
            @played="$emit('played', $event)"
        />
        <Youtube
            v-if="isYoutube"
            ref="movie"
            :src="src"
            v-bind="$attrs"
            @ready="isLoading = false"
            @duration="$emit('duration', $event)"
            @progress="$emit('progress', $event)"
        />
        <Vimeo
            v-else-if="isVimeo"
            ref="movie"
            :src="src"
            v-bind="$attrs"
            @ready="isLoading = false"
            @play="$emit('play', $event)"
            @duration="$emit('duration', $event)"
            @progress="$emit('progress', $event)"
        />
        <div
            v-else-if="embed"
            ref="embed"
            class="oembed-wrapper"
            :style="{ width: wrapperWidth }"
            v-html="embed"
        />
    </div>
</template>

<script>
const DEFAULT_RATIO = 16 / 9;
const POLL_INTERVAL = 5000;
const IFRAMELY_API_KEY = '9e6f0806f449c88a3d732b';

import { useResponsive, getElement } from '@/store/responsive';
import { isYoutube, isVimeo, isLegacyVod, isMux } from '@/helpers/video';

import Txt from '@/components/form/txt';
import Spinner from '@/components/animation/spinner';
import ProgressBar from '@/components/progress/progress-bar';

import Mux from '@/components/media/mux';
import Movie from '@/components/media/movie';
import Vimeo from '@/components/media/vimeo';
import Youtube from '@/components/media/youtube';

export default {
    name: 'Oembed',
    components: { Txt, Mux, Movie, Vimeo, Youtube, Spinner, ProgressBar },
    props: {
        src: { type: String, default: undefined },
        poster: { type: String, default: '' },
        ratio: { type: [Number, Boolean], default: DEFAULT_RATIO },
        controls: { type: Boolean, default: true },
        visible: { type: Boolean, default: true },
        editable: { type: Boolean, default: false },
        title: { type: String, default: '' }
    },
    emits: ['load', 'ready', 'transcoding', 'progress', 'duration', 'play', 'played', 'loaded'],
    data() {
        return {
            embed: null,
            metadata: null,
            wrapperWidth: null,
            previousTime: null,

            isLoading: true,
            isPlaying: false,
            isTranscoding: false
        };
    },
    computed: {
        component() {
            return getElement(this);
        },
        isVimeo() {
            if (!this.src) return;
            return isVimeo(this.src);
        },
        isYoutube() {
            if (!this.src) return;
            return isYoutube(this.src);
        },
        isLegacyVod() {
            if (!this.src) return;
            return isLegacyVod(this.src);
        },
        isMux() {
            if (!this.src) return;
            return isMux(this.src);
        },
        vodId() {
            if (this.isMux) return this.src.split('/').pop();
            const [ _, id ] = this.src.split('/').reverse();
            return id;
        },
        height() {
            if (!this.component.width || !this.ratio) return;
            return this.component.width / this.ratio;
        }
    },
    watch: {
        src: {
            handler(to) {
                if (this.isLegacyVod || this.isMux) return this.getMetadata();
                if ((this.isVimeo || this.isYoutube) && !this.editable) return;
                if (to) this.getEmbedCode(to).then(code => this.embed = code);
            },
            immediate: true
        },
        embed(to) {
            if (to) this.isLoading = false;
        },
        isLoading(to) {
            if (!to) {
                this.isTranscoding = false;
                this.$emit('ready');
                this.$emit('load');
                this.$timeout(() => this.checkEmbed(), 100);
            }
        },
        isTranscoding(to) {
            this.$emit('transcoding', to);
        },
        isPlaying(to) {
            if (to) this.checkProgress();
        }
    },
    mounted() {
        useResponsive(this, this.$el);
    },
    beforeUnmount() {
        useResponsive.removeElement(this);
    },
    methods: {
        pause() {
            if (this.$refs.movie && this.$refs.movie.pause) this.$refs.movie.pause();
        },
        forward(to) {
            if (this.$refs.movie && this.$refs.movie.forward) this.$refs.movie.forward(to);
        },
        getMetadata() {
            return this.$http.get(`/video/status/${this.vodId}`)
                .then(({ data }) => {
                    this.metadata = data;
                    if (data.status === 'COMPLETE') {
                        this.$timeout(() => {
                            this.isLoading = false;
                            this.isTranscoding = false;
                        }, 350);
                    } else {
                        this.isTranscoding = true;
                        this.$timeout(this.getMetadata, POLL_INTERVAL);
                    }
                });
        },
        getOembed(url) {
            let endpoint = '//iframe.ly/api/oembed';
            return this.$http
                .get(endpoint, { params: { url, 'api_key': IFRAMELY_API_KEY }, withCredentials: false })
                .then(result => result.data)
                .catch(() => this.src);
        },
        getFrameCode(src) {
            const iframe = document.createElement('iframe');
            iframe.src = src;
            iframe.title = this.title;
            iframe.setAttribute('frameborder', 0);
            iframe.setAttribute('scrolling', 'yes');
            iframe.setAttribute('allowfullscreen', true);
            iframe.setAttribute('allowscriptaccess', 'always');
            iframe.setAttribute('allownetworking', 'all');
            iframe.setAttribute('allow', 'autoplay; fullscreen; camera; microphone;');
            return iframe.outerHTML;
        },
        getEmbedCode(src) {
            if (!src) return Promise.resolve();
            return this.getOembed(src)
                .then(res => {
                    if (!res) return;
                    this.$emit('loaded', res);

                    // If the src has changed during this async process, ignore the result.
                    if (this.src && this.src !== src) return;

                    // If the response contains HTML, just return it.
                    if (res.html && !res.html.includes('<script')) return res.html;

                    // Photo oEmbed responses do not contain HTML, but we can easily make some.
                    if (res.type === 'photo') {
                        const img = new Image();
                        img.src = res.url || src;
                        img.width = res.width;
                        img.height = res.height;
                        return img.outerHTML;
                    }

                    // If we got here with a Vimeo URL, then that means the video is still transcoding or is private or
                    // something like that. We can get better info from Vimeo if we mess with the URL ourselves a little bit.
                    if (src.startsWith('https://vimeo.com/')) src = src.replace('https://vimeo.com/', 'https://player.vimeo.com/video/');

                    // Uh oh, we didn't get ANY of the useful oEmbed types. :(
                    // Either we got a link response, which is completely useless, or iframe.ly returned an actual error.
                    // In either case, we want to fall back to the generic iframe.
                    return this.getFrameCode(src);
                });
        },
        checkEmbed() {
            if (!(this.$refs.embed && this.$refs.embed.firstElementChild)) return;
            const { height } = this.$refs.embed.firstElementChild.getBoundingClientRect();
            if (height > this.height) this.wrapperWidth = `${Math.round((this.height / height) * 100)}%`;
        }
    }
};
</script>

<style lang="less" scoped>

.oembed {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    .oembed-wrapper, video {
        position: relative;
        width: 100%;
        height: 100%;
    }
    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        background-color: var(--night);
        text-align: center;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        .txt-component.heading.xxxs {
            color: #FFF;
            margin-top: 16px;
            max-width: 200px;
        }
        .spinner { margin: 0; }
        .progress {
            margin: 8px;
            width: 220px;
            max-width: 80%;
        }
    }
}
</style>
<style lang="less">
.oembed .oembed-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    * {
        height: 100% !important;
        padding: 0 !important;
        flex: 1 1 auto;
    }
}
</style>
